export type BulkItem = { id: number; expectedStatus: number; prevStatus: number | null }
export type BulkResult = { id: number; state: 'confirmed' | 'pending' | 'conflict'; attempts: number }
export type BulkIO = {
  read: (id: number) => Promise<number | null>
  write: (item: BulkItem, target: number) => Promise<void>
  save: (results: BulkResult[]) => void
  wait?: (ms: number) => Promise<void>
}

// No batch-size ceiling. Bound database concurrency, not the number of orders.
export async function runBulkStatus (items: BulkItem[], target: number, io: BulkIO): Promise<BulkResult[]> {
  const unique = Array.from(new Map(items.map(item => [item.id, item])).values())
  const results: BulkResult[] = unique.map(item => ({ id: item.id, state: 'pending', attempts: 0 }))
  const wait = io.wait || ((ms: number) => new Promise(resolve => setTimeout(resolve, ms)))
  let cursor = 0
  let checkpointError: unknown
  let stopped = false
  const worker = async () => {
    while (!stopped && cursor < unique.length) {
      const index = cursor++
      const item = unique[index]
      for (let attempt = 1; attempt <= 5; attempt++) {
        if (stopped) return
        results[index].attempts = attempt
        try {
          const current = await io.read(item.id)
          if (current === target) {
            results[index].state = 'confirmed'
            break
          }
          if (current !== item.expectedStatus) {
            results[index].state = 'conflict'
            break
          }
          // The adapter MUST compare the expected state in the UPDATE itself.
          // A timed-out request may have committed; never blindly replay it.
          try { await io.write(item, target) } catch (_) { /* reconcile below */ }
          const actual = await io.read(item.id)
          if (actual === target) {
            results[index].state = 'confirmed'
            break
          }
          if (actual !== item.expectedStatus) {
            results[index].state = 'conflict'
            break
          }
        } catch (_) { /* Unobservable result stays pending, never successful. */ }
        if (attempt < 5) await wait(Math.min(1000 * Math.pow(2, attempt - 1), 8000))
      }
      // Persistence errors stop the runner; do not silently lose the checkpoint.
      if (!stopped) {
        try { io.save(results.map(result => ({ ...result }))) } catch (error) {
          stopped = true
          checkpointError = error
        }
      }
    }
  }
  await Promise.all([worker(), worker()])
  if (stopped) throw checkpointError
  return results
}

export async function collectAllRows<T extends { id: number }> (
  page: (current: number, pageSize: number) => Promise<{ data: T[]; total: number }>
): Promise<T[]> {
  const rows = new Map<number, T>()
  let total: number | undefined
  for (let current = 1; ; current++) {
    const result = await page(current, 200)
    if (total !== undefined && total !== result.total) throw new Error('La lista ha cambiado. Vuelve a seleccionar todos.')
    total = result.total
    const before = rows.size
    result.data.forEach(row => rows.set(row.id, row))
    if (rows.size === total) return Array.from(rows.values())
    if (rows.size > total || rows.size === before || result.data.length < 200) {
      throw new Error('No se ha podido cargar la selección completa. No se ha cambiado ningún pedido.')
    }
  }
}
